import { Box, Heading3, StackView, Text } from 'localmed-core';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { DiButton, DISelect, Input } from '../../../shared/components/DiComponents';
import { DiInputAdapter, DiInputMaskAdapter } from '../../../shared/components/FinalForm';
import { PATIENT_STATUS } from '../../../shared/constants';
import getDaysInMonth from '../../../shared/utils/getDaysInMonth';
import { IStepProps } from '../../pages/SchedulingPage/components/schedulingWizard';
import {
  BackButton,
  ContentWrapper,
  DiRule,
  Label,
  Wrapper,
} from '../../pages/SchedulingPage/components/styles';
import InputError from '../InputError';
import { MONTHS_ITEMS } from './constants';
import {
  constructBirthDay,
  currentYear,
  getMonth,
  insuranceInfoFormCompleted,
  insuranceInfoHasError,
} from './utils';

interface IProps extends IStepProps {
  close: () => void;
}

export default function PatientCoverageFields({ wizard, close }: IProps) {
  if (!wizard) {
    return null;
  }

  const {
    subscriberBirthMonth,
    subscriberBirthDay,
    subscriberBirthYear,
    patientStatus,
  } = wizard.values;

  const { errors } = wizard.form.getState();

  const setBirthDateField = (
    day: string | undefined,
    month: string | undefined,
    year: string | undefined
  ) => {
    if (day && month && year) {
      wizard.form.change('subscriberBirthDate', constructBirthDay(day, month, year));
      wizard.form.mutators.setFieldTouched('subscriberBirthDate', true);
    }
  };

  return (
    <Wrapper>
      <form>
        <ContentWrapper>
          <StackView spacing="lg" width="100%">
            <Heading3 mb="sm">
              <FormattedMessage
                id="scheduling.PatientCoverageFields.PatientCoverageHeading"
                defaultMessage="Insurance Info"
              />
            </Heading3>
            <Box>
              <Label htmlFor="insurancePhone" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.insurancePhoneInputsLabel"
                  defaultMessage="Insurance Phone Number"
                />
              </Label>
              <Field
                name="insurancePhone"
                type="tel"
                id="insurancePhone"
                flex="1 0 0"
                mask="111-111-1111"
                placeholder="###-###-####"
                autoComplete="off"
                component={DiInputMaskAdapter}
              />

              <Text fontSize="sm" color="caption" mt="xxs">
                This is found on your insurance card. If there are multiple numbers, enter the{' '}
                <em>Customer Support</em> number.
              </Text>
              <InputError
                form={wizard.form}
                field="insurancePhone"
                submitField="patientCoverage.insurancePhone"
              />
            </Box>
            <Box>
              <Label htmlFor="memberId" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.memberIdInputsLabel"
                  defaultMessage="Member ID"
                />
              </Label>
              <Field
                name="memberId"
                id="memberId"
                flex="1 0 0"
                autoComplete="off"
                component={DiInputAdapter}
              />
              <InputError form={wizard.form} field="memberId" />
            </Box>
            <Box>
              <Label htmlFor="group" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.groupInputsLabel"
                  defaultMessage="Group Number"
                />
              </Label>
              <Field
                name="group"
                id="group"
                flex="1 0 0"
                autoComplete="off"
                component={DiInputAdapter}
              />
              <InputError form={wizard.form} field="group" />
            </Box>
            <Box>
              <Label htmlFor="employer" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.employerInputsLabel"
                  defaultMessage="Employer Name"
                />
              </Label>
              <Field
                name="employer"
                id="employer"
                flex="1 0 0"
                autoComplete="organization"
                component={DiInputAdapter}
              />
              <InputError form={wizard.form} field="employer" />
            </Box>
            <DiRule />
            <Box>
              <Label id="subscriber-label" htmlFor="subscriberGivenName" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.subscriberNameInputLabel"
                  defaultMessage="Policy Holder’s Name"
                />
              </Label>
              <StackView spacing="sm" direction="row">
                <Field
                  name="subscriberGivenName"
                  placeholder="First"
                  flex="1 0 0"
                  autoComplete="given-name"
                  component={DiInputAdapter}
                />
                <Field
                  name="subscriberFamilyName"
                  aria-labelledby="subscriber-label"
                  placeholder="Last"
                  flex="1 0 0"
                  autoComplete="family-name"
                  component={DiInputAdapter}
                />
              </StackView>
              <InputError form={wizard.form} field="subscriberGivenName" />
              <InputError form={wizard.form} field="subscriberFamilyName" />
            </Box>
            <Box>
              <Label
                id="subscriberBirthMonth-label"
                htmlFor="subscriberBirthMonth"
                mb="sm"
                required
              >
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.subscriberBirthDateLabel"
                  defaultMessage="Policy Holder’s Date of Birth"
                />
              </Label>
              <Field name="subscriberBirthDate">
                {() => (
                  <StackView spacing="sm" direction="row" mb="md">
                    <DISelect
                      id="subscriberBirthMonth"
                      name="subscriberBirthMonth"
                      placeholder="January"
                      autoComplete="bday-month"
                      selectedItem={getMonth(subscriberBirthMonth)}
                      items={MONTHS_ITEMS}
                      onChange={(value) => {
                        wizard.form.batch(() => {
                          wizard.form.change('subscriberBirthMonth', value.id);
                          setBirthDateField(subscriberBirthDay, value.id, subscriberBirthYear);
                        });
                      }}
                    />
                    <Input
                      aria-labelledby="subscriberBirthMonth-label"
                      type="text"
                      min={1}
                      max={getDaysInMonth(subscriberBirthMonth, subscriberBirthYear)}
                      inputMode="numeric"
                      pattern="[0-9]{2}"
                      maxLength={2}
                      id="subscriberBirthDay"
                      name="subscriberBirthDay"
                      flex="1 0 0"
                      placeholder="DD"
                      autoComplete="bday-day"
                      value={subscriberBirthDay}
                      onChange={({ target: { value } }) => {
                        wizard.form.change('subscriberBirthDay', value);
                      }}
                      onBlur={() => {
                        setBirthDateField(
                          subscriberBirthDay,
                          subscriberBirthMonth,
                          subscriberBirthYear
                        );
                      }}
                    />

                    <Input
                      aria-labelledby="subscriberBirthMonth-label"
                      type="text"
                      min={currentYear - 120}
                      max={currentYear}
                      inputMode="numeric"
                      pattern="[0-9]{4}"
                      maxLength={4}
                      id="subscriberBirthYear"
                      name="subscriberBirthYear"
                      flex="1 0 0"
                      placeholder="YYYY"
                      autoComplete="bday-year"
                      value={subscriberBirthYear}
                      onChange={(event) => {
                        wizard.form.change('subscriberBirthYear', event.target.value);
                      }}
                      onBlur={() => {
                        setBirthDateField(
                          subscriberBirthDay,
                          subscriberBirthMonth,
                          subscriberBirthYear
                        );
                        wizard.form.mutators.setFieldTouched('subscriberBirthDate', true);
                      }}
                    />
                  </StackView>
                )}
              </Field>
              <InputError form={wizard.form} field="subscriberBirthDate" />
            </Box>
            <Box>
              <Label htmlFor="subscriberPostalCode" mb="sm" required>
                <FormattedMessage
                  id="scheduling.PatientCoverageFields.policyHolderPostalCodeInputLabel"
                  defaultMessage="Policy Holder’s Postal Code"
                />
              </Label>
              <Field
                name="subscriberPostalCode"
                id="subscriberPostalCode"
                mask="11111"
                flex="1 0 0"
                placeholder="#####"
                autoComplete="postal-code"
                component={DiInputMaskAdapter}
              />
              <InputError
                form={wizard.form}
                field="subscriberPostalCode"
                submitField="patientCoverage.postalCode"
              />
            </Box>
            {patientStatus?.id === PATIENT_STATUS.NEW && (
              <Box>
                <Label htmlFor="subscriberSsn" mb="sm" required>
                  <FormattedMessage
                    id="scheduling.PatientCoverageFields.PolicyHolderSsnInputLabel"
                    defaultMessage="Policy Holder’s SSN"
                  />
                </Label>
                <Field
                  name="subscriberSsn"
                  id="subscriberSsn"
                  mask="111-11-1111"
                  flex="1 0 0"
                  placeholder="###-##-####"
                  autoComplete="off"
                  component={DiInputMaskAdapter}
                />
                <InputError
                  form={wizard.form}
                  field="subscriberSsn"
                  submitField="patientCoverage.ssn"
                />
              </Box>
            )}
          </StackView>
          <Box
            display="flex"
            justifyContent={{ _: 'center', lg: 'space-between' }}
            width="100%"
            my="lg"
          >
            <BackButton
              onClick={close}
              display={{ _: 'none', lg: 'inherit' }}
              variant="subtle"
              size="xl"
            >
              <FormattedMessage
                id="scheduling.PatientCoverageFields.cancelButton"
                defaultMessage="Back"
              />
            </BackButton>
            <DiButton
              onClick={() => {
                wizard.gotoStep(wizard.step + 1);
              }}
              size="xl"
              disabled={!insuranceInfoFormCompleted(wizard.values) || insuranceInfoHasError(errors)}
            >
              <FormattedMessage
                id="scheduling.PatientCoverageFields.NextButton"
                defaultMessage="Next"
              />
            </DiButton>
          </Box>
        </ContentWrapper>
      </form>
    </Wrapper>
  );
}
